import { AUTH_MODE, EXTERNAL_IDP_LOGIN_URL } from "../env"
import { cache, CACHE_KEYS } from "./cache";
import {jwtDecode} from "jwt-decode";

const TOKEN_KEY: string = 'aiagent_access_token'

interface ImpersonationState {
  isImpersonating: boolean
  expiresAt: Date | null
  token: string | null
}

export function getAuthMode(): string {
  return AUTH_MODE
}

function getCookieValue(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

 export function getAuthHeaders(): HeadersInit {
   const token = getAccessToken()
   const impersonate = getImpersonationState().token
   return {
     ...(token    ? { Authorization: `Bearer ${token}` }  : {}),
     ...(impersonate ? { 'x-impersonate': impersonate }   : {}),
   }
 }

export function getImpersonationState(): ImpersonationState {
  let state: ImpersonationState = { isImpersonating: false, expiresAt: null, token: null }
  if (cache.has(CACHE_KEYS.IMPERSONATION_STATE)) {
    const cached = cache.get(CACHE_KEYS.IMPERSONATION_STATE)
    if (cached?.expiresAt?.getTime() < Date.now()) {
      cache.delete(
        CACHE_KEYS.IMPERSONATION_STATE
      )
      return state
    }

    return cached
  }

  const token = getCookieValue('LIQ_Imp_Token')

  if (token) {
    try {
      const payload = jwtDecode<{ exp?: number }>(token)
      if (payload?.exp * 1000 > Date.now()) {
        state = { isImpersonating: true, expiresAt: new Date(payload.exp * 1000), token }
      }
    } catch {
      console.warn('Improperly formatted impersonation token')
    }
  }

  cache.set(CACHE_KEYS.IMPERSONATION_STATE, state)
  return state
}

export function isAuthenticated(): boolean {
  return !!getAccessToken()
}

export function getAccessToken(): string | null {
  const mode = getAuthMode()
  if (mode === 'legacy') {
    return window.localStorage.getItem(TOKEN_KEY)
  } else if (mode === 'external') {
    return getCookieValue('LIQ_Session')
  }
  return null
}

export function setAccessToken(token: string): void {
  window.localStorage.setItem(TOKEN_KEY, token)
}

export function clearAccessToken(): void {
  const mode = getAuthMode()
  if (mode === 'legacy') {
    window.localStorage.removeItem(TOKEN_KEY)
  }
  // In external mode, cookies are typically cleared by the IDP or
  // they are HttpOnly and cannot be cleared via client-side JavaScript.
}

export function logout(): void {
  const mode = getAuthMode()
  clearAccessToken()
  if (mode === 'external') {
    window.location.href = buildExternalLogoutUrl()
  } else {
    window.location.href = '/login'
  }
}

/**
 * Build a login URL for external authentication mode.
 * @param {string} returnPath - The path to return to after authentication
 * @returns {string} Full URL to redirect user to for login
 */
export function buildExternalLoginUrl(returnPath: string = '/home'): string {
  const externalLoginUrl = EXTERNAL_IDP_LOGIN_URL
  if (!externalLoginUrl) {
    console.warn('VITE_EXTERNAL_IDP_LOGIN_URL not configured')
    return '/login'
  }

  // Build callback URL that leadiq-web-app will redirect to
  const callbackUrl = `${window.location.origin}/callback?returnPath=${encodeURIComponent(returnPath)}`

  const externalURL = new URL('/common/login', externalLoginUrl)
  externalURL.searchParams.set('appPath', callbackUrl)

  // Add callback URL as appPath parameter
  return externalURL.toString()
}

function buildExternalLogoutUrl(): string {
  const callbackUrl = `${window.location.origin}/login`
  const externalURL = new URL('/common/logout', EXTERNAL_IDP_LOGIN_URL)
  externalURL.searchParams.set('appPath', callbackUrl)

  return externalURL.toString()
}
